import { ContextSchema, StagedContext } from '@/vantage/types/Context.types';
import { ContextCharacteristic, ExtensionSchema } from '@/vantage/types/Extension.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { InstanceConfig } from '@/vantage/types/TableConfig.types';
import type { SeeqUser } from '@/vantage/utilities';
import { serializeNote } from '@/vantage/utilities';
import _ from 'lodash';
import { Moment } from 'moment';
import {
  EVIDENCE_CONTEXT_LIMIT,
  GraphQL,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE,
  LONG_AGO,
  MAX_POSTGRES_EPOCH_MILLISEC,
} from './constants';
import {
  combineSourceContextGraphql,
  combineSourceContextMt,
  ContextOperation,
  contextWithMetadata,
  createContext,
  datumIdFromContext,
  deleteContext,
  getContextIdPair,
  getContextOperation,
  itemIdFromContext,
  nonEvidenceContextMetadataFilters,
  resolveContext,
  suppressionOpaqueLabel,
  suppressionOpaqueNote,
} from './context';
import contextHooks from './contextHooks';
import * as deps from './dependencies';
import * as graphql from './graphql';
import { useStateWithRef } from './hooks';
import * as setup from './setup';
import { t } from 'i18next';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { VantageNames } from '@/vantage/constants';
import {
  ItemOutputV1,
  sqConditionMonitorsApi,
  sqContextApi,
  sqFormulasApi,
  sqItemsApi,
  sqTableDefinitionsApi,
  TableDefinitionOutputV1,
} from '@/sdk';
import { AnyProperty } from '@/utilities.types';
import { findCategory, findLabels } from '@/vantage/labels';
import { useCallback } from 'react';
import { isVantageNameBasedAssetContextEnabled } from '@/services/systemConfiguration.utilities';
import { AssetBehavior } from '@/vantage/assetBehaviors';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';

export interface Sample {
  key: number;
  value: number;
  is_uncertain?: boolean | null;
}

export interface Signal {
  samples: { samples: Sample[] };
  metadata: AnyProperty;
}

export async function initializeVantageWorkbook(
  workbookWithProperties: ItemOutputV1,
): Promise<{ evidenceTableDefinition: TableDefinitionOutputV1; workbookConfig: InstanceConfig }> {
  let properties = extractWorkbookProperties(workbookWithProperties);
  if (_.isEmpty(properties.tableIds)) {
    properties = await setup.initializeVantage(workbookWithProperties.id);
  }
  const workbookConfig = await getWorkbookConfig(workbookWithProperties.id, properties);
  const { data: evidenceTableDefinition } = await sqTableDefinitionsApi.getTableDefinition({
    id: properties.tableIds.evidence,
  });
  return { evidenceTableDefinition, workbookConfig };
}

function maybeJson<T>(value: string | undefined, defaultValue: T): T {
  return value === undefined ? defaultValue : JSON.parse(value);
}

function extractWorkbookProperties(workbookWithProperties: ItemOutputV1): {
  extensions: ExtensionSchema[];
  includeExtinct: boolean;
  tableIds: AnyProperty<string>;
  nameBasedAssetContextOverride: boolean;
  isManualEventsEnabled: boolean;
} {
  const properties = Object.fromEntries(workbookWithProperties.properties.map(({ name, value }) => [name, value]));
  return {
    extensions: maybeJson(properties.extensions, []),
    includeExtinct: _.isNil(properties.includeExtinct) || properties.includeExtinct === 'true',
    tableIds: maybeJson(properties.tableIds, {}),
    nameBasedAssetContextOverride:
      properties.nameBasedAssetContextOverride !== undefined
        ? properties.nameBasedAssetContextOverride === 'true'
        : isVantageNameBasedAssetContextEnabled(),
    isManualEventsEnabled: maybeJson(properties.isManualEventsEnabled, false),
  };
}

export async function getWorkbookConfig(workbookId: string, properties: AnyProperty<any>): Promise<InstanceConfig> {
  const { install, enabled } = setup.getExtensionConfigs(properties.extensions);

  if (install.length > 0) {
    await setup.installExtensions(workbookId, install);
  }

  return {
    workbookId,
    extensions: enabled,
    includeExtinct: properties.includeExtinct,
    tableConfig: setup.tableConfig(),
    tableIds: properties.tableIds,
    useAssetNameForContextLookup: properties.nameBasedAssetContextOverride,
    isManualEventsEnabled: properties.isManualEventsEnabled,
  };
}

export function useLastFetched(key: string) {
  const [timestamps, setTimestamps] = useStateWithRef<Record<string, Date>>({});
  const getLastFetched = useCallback(() => timestamps.current[key] || LONG_AGO, [timestamps, key]);
  const setLastFetched = useCallback(
    (date: Date) => setTimestamps({ ...timestamps.current, [key]: date }),
    [key, timestamps],
  );
  const resetLastFetched = useCallback(
    () => setTimestamps({ ...timestamps.current, [key]: LONG_AGO }),
    [key, timestamps],
  );
  return { getLastFetched, setLastFetched, resetLastFetched };
}

interface ContextWrapper {
  itemId?: string;
  context: ContextSchema;
  endTime?: string;
  extensionContexts?: any[];
}

function matchesCompositeId(compositeIds: [string, string][], context: ContextWrapper) {
  return compositeIds.some((compositeId) =>
    _.isEqual(compositeId, [context.itemId?.toLowerCase(), context.context.kindId.toLowerCase()]),
  );
}

function isRelated(capsuleIds: string[], context: ContextWrapper) {
  const related = context.context?.related?.map((item) => item.capsuleId);
  return related ? capsuleIds.some((capsuleId) => related.includes(capsuleId)) : false;
}

function isActive(context: ContextWrapper, extensions: ExtensionSchema[], conditionMonitorId: string | undefined) {
  const identifier = context.context.identifier;
  if (identifier === VantageNames.Extensions.Notes) {
    // For suppression opaque, only return notes of the suppressions belonging in this Condition Monitor
    return context.extensionContexts &&
      context.extensionContexts.map((ctx) => ctx.identifier).includes(VantageNames.Extensions.Suppression)
      ? context.context.conditionMonitorId === conditionMonitorId
      : true;
  }

  const extension = extensions.filter((ext) => ext.name === identifier)[0];
  if (!extension) {
    return false;
  }

  const characteristic = extension.contextCharacteristics?.filter(
    (c) => c.kind.toLowerCase() === context.context.kind.toLowerCase(),
  )[0];
  if (characteristic?.eventTimeRepresentation === 'investigationTime') {
    return context.endTime === new Date(MAX_POSTGRES_EPOCH_MILLISEC).toISOString();
  }

  return true;
}

const conditionNameCache = new Map<string, string>();
const getConditionName = async (conditionId: string) => {
  if (conditionNameCache.has(conditionId)) {
    return conditionNameCache.get(conditionId)!;
  }
  const conditionName = (await sqItemsApi.getProperty({ id: conditionId, propertyName: 'name' })).data.value;
  conditionNameCache.set(conditionId, conditionName!);
  return conditionName;
};

async function getSuppressionSystemLabelIds() {
  const suppressionLabelCategory = await findCategory(
    `${SeeqNames.SeeqContextNameSpace.UsageRestrictedLabelNamespace}suppression`,
  );
  const suppressionSystemLabels = suppressionLabelCategory
    ? await findLabels({ categoryId: suppressionLabelCategory.id! })
    : [];
  return suppressionSystemLabels!.map((label) => label.id!);
}

async function createSuppressionOpaque(labels: AnyProperty[], roomConditionMonitorId: string) {
  const suppressionSystemLabelIds = await getSuppressionSystemLabelIds();
  const suppressionLabels = labels.filter(
    (context) =>
      suppressionSystemLabelIds.includes(context.context) && context.datumId === roomConditionMonitorId.toLowerCase(),
  );

  const { data: conditionMonitor } = await sqConditionMonitorsApi.getConditionMonitor({ id: roomConditionMonitorId });
  return Promise.all(
    suppressionLabels.map(async (context) => {
      const creatorName = context.createdBy?.fullName ?? 'Unable to retrieve creator name';
      const conditionName = await getConditionName(context.itemId);
      if (context.label === VantageNames.SuppressionLabel.ONGOING) {
        const suppressionExpiration = conditionMonitor.suppressedConditions!.find(
          (suppression) => suppression.conditionId === context.itemId.toLowerCase(),
        )?.suppressionEndTime;
        return suppressionOpaqueLabel(context, creatorName, conditionName!, suppressionExpiration!);
      } else if (context.label === VantageNames.SuppressionLabel.ENDED_BY_USER) return null;
      else return suppressionOpaqueNote(context, creatorName, conditionName!);
    }),
  );
}
function markAsCapsuleContext(rows: AnyProperty[]): AnyProperty[] {
  return rows.map((r) => {
    return { ...r, isCapsuleContext: true };
  });
}

const capsuleContextItemDatumId = (row: any) => `${row.itemId}:${row.datumId}`;

export async function fetchActiveContext(
  selectedNodes: SimplifiedNode[],
  extensions: ExtensionSchema[] | undefined,
  conditionMonitorId: string | undefined,
  assetBehavior: AssetBehavior,
  cancellationGroup?: string,
): Promise<ContextSchema[]> {
  const leafCapsules = selectedNodes.flatMap((node) => node.leafCapsules);
  const capsuleIds = selectedNodes.map((node) => node.selectedRow[VantageNames.Columns.CapsuleID]);
  const compositeCapsuleIds: [string, string][] = leafCapsules.map((capsule) => [
    capsule[VantageNames.Columns.ConditionID].toLowerCase(),
    capsule[VantageNames.Columns.DatumID].toLowerCase(),
  ]);

  const viewPurviewIdPairs = selectedNodes.flatMap((node) =>
    _.chain(node.viewPurviews)
      .flatMap((viewPurview) => Object.entries(viewPurview))
      .flatMap(([kind, id]) =>
        id &&
        (kind === VantageNames.Kinds.Condition || (kind === VantageNames.Kinds.Asset && assetBehavior.isIdBased()))
          ? [[id]]
          : [],
      )
      .uniqWith(_.isEqual)
      .value(),
  );
  const assetIds = await assetBehavior.selectedAssetIds(selectedNodes);
  const idPairs = _.chain(viewPurviewIdPairs)
    .concat(assetIds?.map((id) => [id]))
    .uniqWith(_.isEqual)
    .value();

  // Query evidence context for the composite capsule IDs separately so we can identify them as capsule context
  const {
    opaque: evidenceOpaqueRaw,
    comments: evidenceCommentsRaw,
    labels: evidenceLabelsRaw,
  } = await graphql.allContextForIdPairs(compositeCapsuleIds, cancellationGroup);

  const evidenceOpaque = markAsCapsuleContext(evidenceOpaqueRaw);
  const evidenceComments = markAsCapsuleContext(evidenceCommentsRaw);
  const evidenceLabels = markAsCapsuleContext(evidenceLabelsRaw);

  const { opaque, comments, labels } = await graphql.allContextForIdPairs(
    idPairs as [string, string][],
    cancellationGroup,
  );

  // Filter out capsule context from the non-evidence context to avoid duplication
  const nonEvidenceOpaque = opaque.filter((r) => !capsuleIds.includes(capsuleContextItemDatumId(r)));
  const nonEvidenceComments = comments.filter((r) => !capsuleIds.includes(capsuleContextItemDatumId(r)));
  const nonEvidenceLabels = labels.filter((r) => !capsuleIds.includes(capsuleContextItemDatumId(r)));

  const suppressionOpaque = conditionMonitorId ? await createSuppressionOpaque(labels, conditionMonitorId) : [];

  const filteredContext = contextWithMetadata(
    [...evidenceOpaque, ...nonEvidenceOpaque].concat(suppressionOpaque.filter((ctx) => ctx !== null)),
    combineSourceContextGraphql(
      [...evidenceComments, ...nonEvidenceComments],
      [...evidenceLabels, ...nonEvidenceLabels],
    ),
  ).filter((details) => {
    return (
      ((details.context.kind === VantageNames.Kinds.Capsule &&
        matchesCompositeId(compositeCapsuleIds as [string, string][], details)) || //capsule context matching capsule composite ID
        isRelated(capsuleIds, details) || // and notes which are related through capsuleIds
        (details.context.kind !== VantageNames.Kinds.Capsule &&
          (details.context.identifier === VantageNames.Extensions.Notes || // non-capsule notes
            selectedNodes[0].kind === details.context.kind))) && // and non-notes matching selected kind
      isActive(details, extensions ?? [], conditionMonitorId)
    );
  });
  return _.sortBy(filteredContext, (context) => context.timeCreated);
}

export async function fetchNonEvidenceContext(
  getLastFetched: () => Date,
  extensions: ExtensionSchema[] | undefined,
  conditionMonitorId: string | undefined,
  nonEvidenceViewPurviews: AnyProperty,
  cancellationGroup?: string,
): Promise<ContextSchema[]> {
  const purviewIds = Object.values(nonEvidenceViewPurviews as object).flatMap((ids: string[]) =>
    ids.map((id): [string] => [id]),
  );

  const lastFetched = getLastFetched();

  // Only include archived when doing an incremental fetch
  const includeArchived = lastFetched !== LONG_AGO;

  const archivedFilter = includeArchived
    ? null
    : graphql.andFilters(
        // No end time for non-capsule labels indicates that the context is active
        graphql.equal(
          VantageNames.TableQueryContextColumns.ContextLabelsEndTime,
          MAX_POSTGRES_EPOCH_MILLISEC * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
        ),
        graphql.equal(VantageNames.TableQueryContextColumns.ContextLabelsArchived, false),
      );

  const { labels } = await graphql.labelContextForIdPairs(
    purviewIds,
    {
      labelFilter: graphql.andFilters(
        graphql.valueFilter(
          VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
          GraphQL.FilterTypes.GreaterThanOrEqual,
          lastFetched.toISOString(),
        ),
        archivedFilter,
      ),
    },
    cancellationGroup,
  );

  const suppressionOpaque = conditionMonitorId ? await createSuppressionOpaque(labels, conditionMonitorId) : [];
  const filters = nonEvidenceContextMetadataFilters(
    extensions?.filter((ext) => !!ext.contextCharacteristics && ext.name !== VantageNames.Extensions.Notes) ?? [],
  );

  const filteredContext = contextWithMetadata(
    suppressionOpaque.filter((ctx) => ctx !== null),
    combineSourceContextGraphql([], labels),
  ).filter((context) => filters.some((filter) => filter(context)));
  return _.sortBy(filteredContext, (context) => context.timeUpdated);
}

export async function fetchEvidenceContext(
  getLastFetched: () => Date,
  evidenceTableId: string | undefined,
  start: Moment,
  end: Moment,
  includeExtinct: boolean,
  cancellationGroup?: string,
): Promise<ContextSchema[]> {
  if (!evidenceTableId) {
    return [];
  }

  const lastFetched = getLastFetched();

  // Only include archived when doing an incremental fetch
  const includeArchived = lastFetched !== LONG_AGO;

  const { data: labels } = await graphql.getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextLabelsId,
        VantageNames.TableQueryContextColumns.ContextLabelsLabelName,
        VantageNames.TableQueryContextColumns.ContextLabelsCategoryName,
        VantageNames.TableQueryContextColumns.ContextLabelsCreatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsArchived,
      ],
      filter: graphql.andFilters(
        graphql.evidenceLabelsFilter(start, end, lastFetched, includeExtinct, includeArchived),
        graphql.isNotNull(VantageNames.TableQueryContextColumns.ContextLabelsId),
      ),
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE,
    },
    `${cancellationGroup}-labels`,
  );

  const { data: opaque } = await graphql.getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextOpaqueId,
        VantageNames.TableQueryContextColumns.ContextOpaqueData,
        VantageNames.TableQueryContextColumns.ContextOpaqueKey,
        VantageNames.TableQueryContextColumns.ContextOpaqueStartTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueEndTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueCreatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
      ],
      filter: graphql.andFilters(
        graphql.evidenceOpaqueFilter(start, end, lastFetched, includeExtinct, includeArchived),
        graphql.isNotNull(VantageNames.TableQueryContextColumns.ContextOpaqueId),
      ),
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE,
    },
    `${cancellationGroup}-opaque`,
  );

  const evidenceOpaque = markAsCapsuleContext(opaque);
  const evidenceLabels = markAsCapsuleContext(labels);

  return _.sortBy(
    contextWithMetadata(evidenceOpaque, combineSourceContextMt([], evidenceLabels)),
    (context) => context.timeUpdated,
  );
}

interface PrePostHooks {
  pre?: (params: any) => Promise<any>;
  post?: (params: any) => Promise<any>;
}

interface ContextHook {
  creation?: PrePostHooks;
  resolution?: PrePostHooks;
  deletion?: PrePostHooks;
}

async function withContextHooks(
  { pre, post }: PrePostHooks = { pre: undefined, post: undefined },
  params: any = null,
  fn: () => Promise<any> = Promise.resolve,
) {
  if (pre) await pre(params);
  await fn();
  if (post) await post(params);
}

const serializeRelated = (
  stagedContext: StagedContext[],
  node: SimplifiedNode,
  extensions: ExtensionSchema[],
  existingEvidenceContextKeys: string[],
) =>
  stagedContext
    .flatMap((ctx) => {
      const extension = extensions.find((ext) => ext.name === ctx.identifier);
      const isProxy = extension?.contextCharacteristics?.find((item) => item.kind === node.kind)?.proxyKind === 'leaf';
      const isNote = extension?.name === VantageNames.Extensions.Notes;
      const isDeletion = Object.prototype.hasOwnProperty.call(ctx, 'deletionIds');

      return node.leafCapsules
        .filter((leaf) => {
          if (isNote || !isProxy) {
            return false;
          }
          const leafHasContext = existingEvidenceContextKeys.includes(
            `${leaf[VantageNames.Columns.CapsuleID]}:${ctx.identifier}`,
          );
          return isDeletion ? leafHasContext : !leafHasContext;
        })
        .map((item) => `${item[VantageNames.Columns.CapsuleID]}:${ctx.identifier}`);
    })
    .join(',');

export async function submitContext(
  stagedContext: StagedContext[],
  selectedNodes: SimplifiedNode[],
  start: Moment,
  end: Moment,
  getLastFetched: () => Date,
  extensions: ExtensionSchema[],
  contextItemIdMapping: Record<string, string> | undefined,
  tableIds: AnyProperty<string> | undefined,
  user: SeeqUser | undefined,
  conditionMonitorId: string | undefined,
  activeContext: ContextSchema[],
  evidenceContext: ContextSchema[],
) {
  const existingEvidenceContextKeys = evidenceContext.map(
    (condition) => `${condition.conditionId}:${condition.kindId}:${condition.identifier}:${condition.content.value}`,
  );

  // This ensures we create a note, but we should find a way to handle this without a note later - AD-XXXX
  if (!stagedContext.find((ctx) => ctx.identifier === VantageNames.Extensions.Notes)) {
    const stagedContextCopy = [...stagedContext];
    stagedContext.push({
      identifier: VantageNames.Extensions.Notes,
      content: { value: ' ' },
      extensionContexts: stagedContextCopy,
    });
  }

  const deletionOperations: Promise<void>[] = [];
  const deletedIds = new Set<string>();

  await Promise.all(
    selectedNodes.map(async (node) => {
      await Promise.all(
        stagedContext.map(async (ctx) => {
          if (!extensions) return;

          const extension = extensions.find((ext) => ext.name === ctx.identifier);
          if (!extension) return;

          const characteristic = extension.contextCharacteristics?.find(
            (characteristic) => characteristic.kind === node.kind,
          );
          if (!characteristic) return;

          const operation = getContextOperation(ctx, characteristic);
          const { resolution, deletion } = (contextHooks as Record<string, ContextHook>)[ctx.identifier] || {};
          const hookParams = { node, context: ctx, extension, tableIds };

          if (operation === ContextOperation.Resolve || operation === ContextOperation.Delete) {
            ctx.deletionIds?.forEach((deletionId) => {
              if (!deletedIds.has(deletionId)) {
                deletedIds.add(deletionId);

                if (operation === ContextOperation.Resolve) {
                  deletionOperations.push(
                    withContextHooks(resolution, hookParams, () => resolveContext(deletionId, activeContext)),
                  );
                } else {
                  deletionOperations.push(
                    withContextHooks(deletion, hookParams, () =>
                      deleteContext(deletionId, activeContext, characteristic.archiveChildren),
                    ),
                  );
                }
              }
            });
          }
        }),
      );
    }),
  );

  await Promise.all(deletionOperations);

  const creationOperations: Promise<void>[] = [];

  await Promise.all(
    selectedNodes.map(async (node) => {
      const serializedRelated = serializeRelated(stagedContext, node, extensions ?? [], existingEvidenceContextKeys);
      const serializedContext = serializeNote(stagedContext);

      await Promise.all(
        stagedContext.map(async (ctx) => {
          if (!extensions) return;

          const extension = extensions.find((ext) => ext.name === ctx.identifier);
          if (!extension) return;

          const characteristic = extension.contextCharacteristics?.find(
            (characteristic) => characteristic.kind === node.kind,
          );
          if (!characteristic) return;

          const operation = getContextOperation(ctx, characteristic);
          const { creation } = (contextHooks as Record<string, ContextHook>)[ctx.identifier] || {};
          const hookParams = { node, context: ctx, extension, tableIds };

          if (operation === ContextOperation.Add) {
            const [itemId, datumId] = await getContextIdPair(node);
            creationOperations.push(
              withContextHooks(creation, hookParams, () =>
                createContext({
                  ctx,
                  characteristic,
                  node,
                  serializedRelated,
                  serializedContext,
                  user,
                  existingEvidenceContextKeys,
                  activeContext: activeContext?.filter((act) => act.identifier === ctx.identifier),
                  itemId: itemId!,
                  datumId,
                  conditionMonitorId: conditionMonitorId!,
                }),
              ),
            );
          }
        }),
      );
    }),
  );

  await Promise.all(creationOperations);
}

export async function togglePinContext({
  contextId,
  activeContext,
}: {
  contextId: string;
  activeContext: ContextSchema[];
}) {
  const context = activeContext?.filter((ctx) => ctx.id === contextId)[0];
  const itemId = itemIdFromContext(context);
  const datumId = datumIdFromContext(context);
  const opaqueMatches = await graphql.context(itemId, datumId);
  const opaque = opaqueMatches.find((ctx) => ctx.contextId === context.metadataId);
  if (!opaque) {
    throw new Error(t('VANTAGE.ERROR.CONTEXT_WITH_ID', { contextId }));
  }
  const { contextId: opaqueId, context: data, key } = opaque;
  const ctx = JSON.parse(data);
  await sqContextApi.updateContextOpaque(
    {
      datumId,
      key,
      data: JSON.stringify({ ...ctx, starred: !ctx.starred }),
    },
    {
      itemId,
      opaqueId,
    },
  );
}

export function addCharacteristicsForPurviewFields(extension: ExtensionSchema, purviewFields: string[]) {
  const template = extension.contextCharacteristics?.[0];
  const kinds = extension.contextCharacteristics?.map((c) => c.kind) ?? [];
  const newPurviewFields: ContextCharacteristic[] =
    purviewFields
      ?.filter((field) => !kinds.includes(field))
      ?.map((field) => ({ ...template, kind: field, eventTimeRepresentation: undefined })) ?? [];
  return {
    ...extension,
    contextCharacteristics: newPurviewFields
      ? extension.contextCharacteristics?.concat(newPurviewFields)
      : extension.contextCharacteristics,
  };
}

const QUICK_TREND_TYPES = ['CalculatedScalar', 'CalculatedSignal', 'StoredScalar', 'StoredSignal'];

export async function fetchQuickTrend(start: Moment, end: Moment, conditionIds: string[]): Promise<any[]> {
  const laneWidth = 2000;
  const dependencies = _.chain(await Promise.all(conditionIds.map((id) => deps.getDependencies(id))))
    .flatMap((ds) => deps.flatten(ds))
    .filter((d) => QUICK_TREND_TYPES.includes(d.type))
    .uniqBy((d) => d.fingerprint)
    .value();
  if (dependencies.length === 0) {
    throw new Error(t('VANTAGE.ERROR.NO_SIGNALS'));
  } else if (dependencies.length > 10) {
    throw new Error(t('VANTAGE.ERROR.TOO_MANY_SIGNALS'));
  }

  const duration = end.valueOf() - start.valueOf();
  const resolution = duration / laneWidth;
  return await Promise.all(
    dependencies.map(async (dep) => {
      const {
        data: { metadata, samples },
      } = await sqFormulasApi.runFormula({
        start: start.toISOString(),
        end: end.toISOString(),
        formula: ['StoredScalar', 'CalculatedScalar'].includes(dep.type)
          ? `$sig = $param.toSignal() $sig.spikeCatcher(${resolution}ms).parallelize()`
          : `$param.spikeCatcher(${resolution}ms).parallelize()`,
        parameters: [`param=${dep.id}`],
        limit: laneWidth * 5,
      });
      return [{ [dep.id]: deps.assetInfo(dep) }, { metadata, samples }];
    }),
  );
}
